<template>
  <div ref="componentRef" class="min-h-screen bg-white">
    <div class="max-w-4xl mx-auto px-4 py-16">
      <div v-if="loading" class="text-center py-16">
        <Loader size="large" />
      </div>
      <div v-else-if="career" class="space-y-8">
        <div>
          <h1 class="text-4xl md:text-5xl font-header font-bold text-primary mb-4">
            {{ career.title }}
          </h1>
        </div>

        <div class="prose max-w-none space-y-[0.196rem]">
          <div v-if="career.description" class="p-2.5 md:p-4">
            <h2 class="text-2xl font-header font-bold text-primary mb-1">Job Description</h2>
            <div class="text-gray-700 font-sans leading-relaxed whitespace-pre-line">{{ career.description }}</div>
          </div>

          <div v-for="(subsection, index) in career.subsections" :key="index" class="p-2.5 md:p-4">
            <h2 class="text-2xl font-header font-bold text-primary mb-1">{{ subsection.title }}</h2>
            <div class="text-gray-700 font-sans leading-relaxed whitespace-pre-line">{{ subsection.content }}</div>
          </div>
        </div>

        <div class="pt-8 border-t border-gray-200 flex justify-end">
          <a :href="`/careers/apply/${career.id}`"
            class="bg-green-700 text-white px-8 py-3 rounded-md hover:bg-green-800 transition-colors font-marketing font-semibold whitespace-nowrap">
            Apply Now
          </a>
        </div>
      </div>
      <div v-else class="text-center py-16">
        <h2 class="text-2xl font-header font-bold text-primary mb-4">Job Not Found</h2>
        <p class="text-gray-600 font-sans mb-6">The job you are looking for does not exist or has been removed.</p>
        <a href="/careers" class="text-green-700 hover:text-green-800 font-marketing underline">Back to Careers</a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { publicService } from '../services/public'
import Loader from './Loader.vue'
import { useLoadingState } from '../composables/useLoadingState'
import { useDataTranslation } from '../composables/useDataTranslation'

const props = defineProps({
  careerId: {
    type: String,
    required: true
  }
})

const componentRef = ref(null)
const career = ref(null)
const { loading, setLoading } = useLoadingState(true)

const { translatePage, handleLanguageChange: handleTranslation } = useDataTranslation(componentRef, loading, '.min-h-screen.bg-white', setLoading)

const handleLanguageChange = async () => {
  await loadCareer()
  await handleTranslation()
}

onMounted(() => {
  window.addEventListener('language-changed', handleLanguageChange)
})

onUnmounted(() => {
  window.removeEventListener('language-changed', handleLanguageChange)
})

const loadCareer = async () => {
  setLoading(true)
  try {
    const data = await publicService.getCareer(parseInt(props.careerId))
    career.value = data
  } catch (error) {
    console.error('Failed to load career:', error)
    career.value = null
  } finally {
    setLoading(false)
  }
}

watch(() => props.careerId, () => {
  loadCareer()
}, { immediate: false })

onMounted(async () => {
  await loadCareer()
  await translatePage()
})
</script>

<style scoped></style>
